import React from 'react';
import { FormTextRegular } from '../form/FormText';
import { FormEmail } from '../form/FormEmail';
import { CheckboxCheck } from '../form/FormCheckboxCheck';
import { FormPhone } from '../form/FormPhone';
import { useDisclosureDocumentSigningLink } from '@app/hooks/useSigningLink';
import Button from '@app/common/components/Button';
import { Spinner, SpinnerSizes } from '@app/common/design-system/spinner';
import cx from 'classnames';
import styles from './DisclosureDocumentStyles.module.scss';

const CopySigningLinkButton = ({ documentId, hasSigned }) => {
    const { copySigningLink, isSigningLinkLoading } = useDisclosureDocumentSigningLink(documentId);

    return (
        <Button
            type="button"
            disabled={isSigningLinkLoading || hasSigned}
            link
            onClick={copySigningLink}
            tooltip={hasSigned ? 'Recipient has already signed.' : undefined}
            className={styles.copySigningLink}
        >
            <span className={styles.signingLinkText}>
                {isSigningLinkLoading && <Spinner size={SpinnerSizes.SMALL} />}
                Copy signing link
            </span>
        </Button>
    );
};

const DisclosureRentalProviderGroup = ({
    index,
    removeRentalProvider,
    disabled,
    values,
    handlePrimaryLandlordSelect,
    isSigningLinkActive,
    documentId
}) => {
    const isPrimary = values.landlords[index].isPrimaryLandlord;
    const hasSigned = values.landlords[index].hasSigned;

    return (
        <div className="form-section">
            <div className={cx([styles.disclosureDocHeaderRow, index !== 0 && 'form-section__header'])}>
                <h3 className="form-section__title">Rental provider {index + 1}</h3>
                {(isPrimary && isSigningLinkActive) || (!disabled && index !== 0) ? (
                    <div className="actions">
                        {isPrimary && isSigningLinkActive && (
                            <CopySigningLinkButton documentId={documentId} hasSigned={hasSigned} />
                        )}
                        {!disabled && index !== 0 && (
                            <Button type="button" onClick={() => removeRentalProvider(index)}>
                                <span className="span-remove" onClick={() => removeRentalProvider(index)}>
                                    Remove
                                </span>
                            </Button>
                        )}
                    </div>
                ) : null}
            </div>
            <div className="form-section__body">
                <div className="formBox-column">
                    <div className="nowrap larger">
                        <FormTextRegular
                            name={`landlords[${index}].firstName`}
                            label="First Name"
                            required
                            disabled={disabled}
                        />
                        <FormTextRegular
                            name={`landlords[${index}].lastName`}
                            label="Last Name"
                            required
                            disabled={disabled}
                        />
                    </div>
                    <div className="nowrap larger">
                        <FormEmail
                            name={`landlords[${index}].email`}
                            label="Email"
                            required={values.landlords[index].isPrimaryLandlord}
                            disabled={disabled}
                            tooltip="Email address the finalised document will be sent to"
                        />
                        {values.landlords[index].isPrimaryLandlord && (
                            <FormPhone
                                name={`landlords[${index}].phone`}
                                dataTest={`landlords-${index}-phone`}
                                label="Mobile"
                                required
                                disabled={disabled}
                                tooltip="If there are multiple rental providers, it will only be sent to one RRP (primary) to capture answers to the Disclosure doc"
                                tooltipPlace="right"
                            />
                        )}
                    </div>
                    <CheckboxCheck
                        name={`landlords[${index}].isPrimaryLandlord`}
                        label="Primary rental provider"
                        value={`persons[${index}].isPrimaryLandlord`}
                        onClick={value => handlePrimaryLandlordSelect(index, value)}
                        disabled={disabled}
                    />
                </div>
            </div>
        </div>
    );
};

export default DisclosureRentalProviderGroup;
